import { useId, useMemo, useState } from 'react';

// Hafif SVG alan/çizgi grafiği — harici kütüphane yok (recharts vb. eklenmedi).
// data: [{ t: ISOstring|Date, v: number }]
export default function TrendChart({ data = [], height = 200, color = 'var(--accent)', valueFormat }) {
  const [hover, setHover] = useState(null);
  const gradId = `tcArea-${useId().replace(/:/g, '')}`;
  const W = 640;
  const H = height;
  const padX = 8;
  const padY = 16;

  const pts = useMemo(() => {
    const clean = (data || [])
      .map((d) => ({ t: new Date(d.t).getTime(), v: Number(d.v) }))
      .filter((d) => Number.isFinite(d.t) && Number.isFinite(d.v))
      .sort((a, b) => a.t - b.t);
    return clean;
  }, [data]);

  if (pts.length < 2) {
    return (
      <div style={{ height: H, display: 'grid', placeItems: 'center', color: 'var(--text-3)', fontSize: 13 }}>
        —
      </div>
    );
  }

  const minT = pts[0].t;
  const maxT = pts[pts.length - 1].t;
  const maxV = Math.max(...pts.map((p) => p.v), 1);
  const minV = Math.min(...pts.map((p) => p.v), 0);
  const spanV = maxV - minV || 1;

  const x = (t) => padX + ((t - minT) / (maxT - minT || 1)) * (W - padX * 2);
  const y = (v) => padY + (1 - (v - minV) / spanV) * (H - padY * 2);

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.t).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${x(maxT).toFixed(1)} ${H - padY} L ${x(minT).toFixed(1)} ${H - padY} Z`;

  const fmt = valueFormat || ((n) => n.toLocaleString('tr-TR'));

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let nearest = pts[0];
    let best = Infinity;
    for (const p of pts) {
      const d = Math.abs(x(p.t) - px);
      if (d < best) { best = d; nearest = p; }
    }
    setHover(nearest);
  };

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: H, display: 'block', overflow: 'visible' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1={padX} y1={padY + g * (H - padY * 2)} x2={W - padX} y2={padY + g * (H - padY * 2)}
            stroke="var(--border)" strokeDasharray="2 5" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke"
          strokeLinecap="round" strokeLinejoin="round" />
        {hover && (
          <g>
            <line x1={x(hover.t)} y1={padY} x2={x(hover.t)} y2={H - padY} stroke="var(--border-strong)" vectorEffect="non-scaling-stroke" />
            <circle cx={x(hover.t)} cy={y(hover.v)} r="3.5" fill={color} />
          </g>
        )}
      </svg>
      {hover && (
        <div
          style={{
            position: 'absolute', top: 0, left: `${(x(hover.t) / W) * 100}%`,
            transform: 'translateX(-50%)', pointerEvents: 'none',
            background: 'var(--surface-3)', border: '1px solid var(--border-strong)', borderRadius: 8,
            padding: '4px 8px', fontSize: 11, color: 'var(--text)', whiteSpace: 'nowrap',
          }}
        >
          <strong>{fmt(hover.v)}</strong>
          <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>
            {new Date(hover.t).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}
    </div>
  );
}
